import type { AiqIndexReport } from '../../types/aiq-report'
import { BellCurveChart } from './BellCurveChart'

const PRE_COLOR = '#3b82f6'
const POST_COLOR = '#8b5cf6'

interface AiqIndexSectionProps {
  report: AiqIndexReport
  phase: 'pre' | 'post' | 'comparison'
}

/**
 * AIQ 지수 섹션 — Advanced score를 IQ-style 분포(N(100, 15))로 변환한 지수 카드 + Bell Curve.
 *
 * phase에 따라 Pre/Post/비교를 표시합니다. ReportTabLayout의 Advanced 블록 아래에
 * 렌더링되며, 부모가 탭 상태를 관리합니다.
 */
export function AiqIndexSection({ report, phase }: AiqIndexSectionProps) {
  const { preIndex, postIndex, improvement, estimatedMean, estimatedSigma } = report

  // Which number card to show
  const renderNumberCard = () => {
    if (phase === 'pre') {
      return (
        <IndexBadge
          label="Pre-AIQ 지수"
          value={preIndex}
          color={PRE_COLOR}
        />
      )
    }
    if (phase === 'post') {
      return (
        <IndexBadge
          label="Post-AIQ 지수"
          value={postIndex}
          color={POST_COLOR}
        />
      )
    }
    // comparison
    return (
      <div className="flex items-center gap-4">
        <IndexBadge label="Pre-AIQ" value={preIndex} color={PRE_COLOR} />
        <span className="text-2xl text-gray-400" aria-hidden>
          →
        </span>
        <IndexBadge label="Post-AIQ" value={postIndex} color={POST_COLOR} />
        {improvement != null && (
          <div
            className={`ml-2 text-sm font-semibold ${
              improvement > 0
                ? 'text-green-600'
                : improvement < 0
                  ? 'text-red-500'
                  : 'text-gray-500'
            }`}
          >
            {improvement > 0 ? '▲' : improvement < 0 ? '▼' : '−'}{' '}
            {Math.abs(improvement).toFixed(1)}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Intro + parameter badges */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <p className="text-xs text-gray-500 max-w-prose">
          Advanced 점수를 일반 IQ 분포(평균 100, 표준편차 15)로 변환한 값입니다.
          표준 IQ와 같은 해석이 가능합니다.
        </p>
        <span
          className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-gray-100 text-[10px] text-gray-500 font-mono shrink-0"
          title="변환에 사용된 모집단 파라미터 (조직 설정 또는 기본값)"
        >
          μ={estimatedMean.toFixed(1)}, σ={estimatedSigma.toFixed(1)}
        </span>
      </div>

      {/* Numeric card */}
      <div className="flex justify-center">{renderNumberCard()}</div>

      {/* Bell curve */}
      <BellCurveChart preIndex={preIndex} postIndex={postIndex} phase={phase} />

      {/* Legend for comparison */}
      {phase === 'comparison' && preIndex != null && postIndex != null && (
        <div className="flex items-center justify-center gap-6 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ backgroundColor: PRE_COLOR }}
            />
            Pre-AIQ
          </span>
          <span className="flex items-center gap-1">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ backgroundColor: POST_COLOR }}
            />
            Post-AIQ
          </span>
        </div>
      )}
    </div>
  )
}

function IndexBadge({
  label,
  value,
  color,
}: {
  label: string
  value: number | null
  color: string
}) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-xs text-gray-500">{label}</span>
      <span
        className="text-3xl font-extrabold tabular-nums"
        style={{ color }}
      >
        {value != null ? Math.round(value) : '—'}
      </span>
    </div>
  )
}
